<template>
  <div class="w-100 configuration-calendar grid-container">
    <configuration-section-header
      class="my-0"
      :section-icon="'far fa-calendar-alt'"
      :shortcuts-button="false"
      :section-title="$t('configuration.sections.calendar')"
      :show-display-options="false"
    >
      <b-button
        size="sm"
        class="btn--primary mr-2"
        @click="$refs.configurationWorkDaysModalWrapper.show([daysOfWeekForWeekendDays])"
      >
        <i
          class="fal fa-calendar-alt mr-2"
          aria-hidden="true"
        />
        {{ `${$t('configuration.modal.calendar.workDays')} (${daysOfWeekForWeekendDays.length})` }}
      </b-button>
      <b-button
        v-shortkey="['alt', 'enter']"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="$refs.configurationEventModalWrapper.show()"
        @shortkey="$refs.configurationEventModalWrapper.show()"
      >
        <i
          v-tooltip="$t('configuration.actions.add') + ' ' + $t('configuration.modal.calendar.publicHoliday')"
          class="far fa-plus"
          aria-hidden="true"
        />
      </b-button>
    </configuration-section-header>
    <b-container
      fluid="lg"
      class="flex-grow-1"
    >
      <b-row class="h-100">
        <b-col>
          <div class="p-4 h-100 d-flex flex-column">
            <div class="calendar-height flex-grow-1">
              <FullCalendar
                :locale="initialLocale"
                :locales="allLocales"
                :first-day="1"
                :plugins="calendarPlugins"
                :height="'parent'"
                :header="calendarHeader"
                :selectable="true"
                :events="events"
                @datesRender="onDatesRender"
                @select="dateClick"
                @eventClick="onEventClick"
              />
            </div>
            <div class="calendar-footer d-flex mt-3">
              <div class="mr-4">
                <i
                  class="fas fa-circle mr-2 festive-color legend-circle"
                  aria-hidden="true"
                />
                <span class="dark">{{ $t('configuration.modal.calendar.publicHolidayLegend') }}</span>
              </div>
              <div class="mr-4">
                <i
                  class="fas fa-circle mr-2 day-off-color legend-circle"
                  aria-hidden="true"
                />
                <span class="dark">{{ $t('configuration.modal.calendar.workDays') }}</span>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-container>
    <modal-wrapper ref="configurationEventModalWrapper">
      <configuration-event-modal
        @createEvent="createLaboratoryHoliday($event)"
        @updateEvent="updateLaboratoryHoliday($event)"
        @deleteEvent="deleteLaboratoryHoliday($event)"
      />
    </modal-wrapper>
    <modal-wrapper ref="configurationWorkDaysModalWrapper">
      <configuration-work-days-modal @updateLaboratoryWeekendDays="updateLaboratoryWeekendDays($event)" />
    </modal-wrapper>
  </div>
</template>

<script>
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader'
import ConfigurationEventModal from '@/modules/configurationModule/detail/laboratory/calendar/ConfigurationEventModal'
import ConfigurationWorkDaysModal from '@/modules/configurationModule/detail/laboratory/calendar/ConfigurationWorkDaysModal'
import FullCalendar from '@fullcalendar/vue'
import allLocales from '@fullcalendar/core/locales-all'
import dayGridPlugin from '@fullcalendar/daygrid'
import listPlugin from '@fullcalendar/list'
import interactionPlugin from '@fullcalendar/interaction'
import LaboratoryHolidayConfigurationServices
    from '@/api/resources/configuration/laboratory/laboratoryHolidayConfigurationServices'
import LaboratoryHolidaysService from '@/api/resources/common/laboratoryHolidaysService'
import LaboratoryWeekendDaysService from '@/api/resources/common/laboratoryWeekendDaysService'
import { EVENT_TYPE } from '@/api/resources/personnelEvents/constants/eventType'
import { DAY_OF_WEEK } from '@/api/resources/personnelEvents/constants/dayOfWeek'
import moment from 'moment'
import { DateUtils } from '@/shared/util/date-utils'
import LaboratoryWeekendDaysConfigurationServices
    from '@/api/resources/configuration/laboratory/laboratoryWeekendDaysConfigurationServices'
import sessionStore from '@/session/sessionStore'

export default {
    name: 'DemographyCountriesGrid',
    components: {
        ConfigurationSectionHeader,
        ConfigurationEventModal,
        ConfigurationWorkDaysModal,
        FullCalendar
    },
    data() {
        return {
            initialLocale: sessionStore.getLocale(),
            allLocales: allLocales,
            actionTitle: null,
            calendarPlugins: [ dayGridPlugin, listPlugin, interactionPlugin ],
            currentDatesRange: null,
            publicHolidays: [],
            weekendDays: [],
            daysOfWeekForWeekendDays: [],
            calendarHeader: {
                center: 'title',
                left: 'today prev,next',
                right: 'dayGridMonth,dayGridWeek,dayGridDay,listYear'
            }
        }
    },
    computed: {
        laboratoryId() {
            return this.$store.getters.labId
        },
        events() {
            return this.publicHolidays.concat(this.weekendDays)
        }
    },
    created() {
        this.getAllLaboratoryWeekendDays()
    },
    methods: {
        onEventClick(clickedEvent) {
            const mappedEvent = {
                id: clickedEvent.event.id,
                name: clickedEvent.event.title,
                startingDate: clickedEvent.event.start,
                endingDate: moment(clickedEvent.event.end).subtract(1, 'days')
            }
            this.$refs.configurationEventModalWrapper.show([mappedEvent])
        },
        dateClick(dateSelection) {
            const mappedEvent = {
                startingDate: dateSelection.startStr,
                endingDate: moment(dateSelection.endStr).subtract(1, 'days')
            }
            this.$refs.configurationEventModalWrapper.show([mappedEvent])
        },
        onDatesRender(data) {
            this.currentDatesRange = { activeStart: data.view.activeStart, activeEnd: data.view.activeEnd }
            this.getAllLaboratoryHolidaysByDateRange()
        },
        mapEvents(events, type) {
            let mappedEvents = []
            for (let event of events) {
                const mappedEvent = {
                    id: event.id,
                    title: event.name,
                    start: event.startingDate,
                    end: DateUtils.formatDate(moment(event.endingDate).add(1, 'days').toDate()),
                    classNames: EVENT_TYPE[type].classNames,
                    allDay: true,
                    displayEventTime: false,
                    editable: true
                }
                mappedEvents.push(mappedEvent)
            }
            return mappedEvents
        },
        async getAllLaboratoryHolidaysByDateRange() {
            this.updateLoading(true)
            const params = {
                from: DateUtils.formatDate(this.currentDatesRange.activeStart),
                to: DateUtils.formatDate(this.currentDatesRange.activeEnd)
            }
            const [error, response] = await this.toCall(LaboratoryHolidaysService.getAllLaboratoryHolidaysByDateRange(params, this.laboratoryId))
            this.updateLoading(false)
            if (error) {
                this.showErrorModalGlobalFromErrorResponse(error)
            }
            this.publicHolidays = this.mapEvents(response, EVENT_TYPE.PUBLIC_HOLIDAY.code)
        },
        async getAllLaboratoryWeekendDays() {
            this.updateLoading(true)
            const [error, response] = await this.toCall(LaboratoryWeekendDaysService.getAllLaboratoryWeekendDays(this.laboratoryId))
            this.updateLoading(false)
            if (error) {
                this.showErrorModalGlobalFromErrorResponse(error)
            }
            let daysOfWeek = []
            for (let weekendDay of response) {
                const dayOfTheWeekCode = weekendDay.dayOfTheWeek.code
                daysOfWeek.push(DAY_OF_WEEK[dayOfTheWeekCode].arrayPosition)
            }
            const mappedWeekendDays = [{
                title: this.$t('configuration.modal.calendar.workDays'),
                classNames: 'day-off',
                rendering: 'background',
                daysOfWeek: daysOfWeek
            }]
            this.daysOfWeekForWeekendDays = daysOfWeek
            this.weekendDays = mappedWeekendDays
        },
        async updateLaboratoryHoliday(event) {
            this.updateLoading(true)
            const request = {
                endingDate: DateUtils.formatDate(event.holidayEndingDateSelected),
                name: event.holidayName,
                startingDate: DateUtils.formatDate(event.holidayStartingDateSelected)
            }
            const [error] = await this.toCall(LaboratoryHolidayConfigurationServices.updateLaboratoryHoliday(request, event.holidayId, this.laboratoryId))
            this.updateLoading(false)
            if (error) {
                this.showErrorModalGlobalFromErrorResponse(error)
                return
            }
            this.showToastSuccessGlobal()
            await this.getAllLaboratoryHolidaysByDateRange()
        },
        async createLaboratoryHoliday(event) {
            this.updateLoading(true)
            const request = {
                endingDate: DateUtils.formatDate(event.holidayEndingDateSelected),
                name: event.holidayName,
                startingDate: DateUtils.formatDate(event.holidayStartingDateSelected)
            }
            const [error] = await this.toCall(LaboratoryHolidayConfigurationServices.createLaboratoryHoliday(request, this.laboratoryId))
            this.updateLoading(false)
            if (error) {
                this.showErrorModalGlobalFromErrorResponse(error)
                return
            }
            this.showToastSuccessGlobal()
            await this.getAllLaboratoryHolidaysByDateRange()
        },
        async updateLaboratoryWeekendDays(weekendDays) {
            this.updateLoading(true)
            const request = {
                weekendDayCodes: weekendDays.filter(weekendDay => weekendDay.selected).map(mappedWeekendDay => mappedWeekendDay.code)
            }
            const [error] = await this.toCall(LaboratoryWeekendDaysConfigurationServices.updateAllLaboratoryWeekendDays(request, this.laboratoryId))
            this.updateLoading(false)
            if (error) {
                this.showErrorModalGlobalFromErrorResponse(error)
                return
            }
            this.showToastSuccessGlobal()
            await this.getAllLaboratoryWeekendDays()
        },
        async deleteLaboratoryHoliday(holidayId) {
            const deleteConfirmation = await this.showConfirmationModalGlobal()
            if (deleteConfirmation) {
                this.updateLoading(true)
                const [error] = await this.toCall(LaboratoryHolidayConfigurationServices.deleteLaboratoryHoliday(holidayId, this.laboratoryId))
                this.updateLoading(false)
                if (error) {
                    this.showErrorModalGlobalFromErrorResponse(error)
                    return
                }
                this.showToastSuccessGlobal(this.$tc('configuration.modal.calendar.onEventDeletedMsg', 1))
                await this.getAllLaboratoryHolidaysByDateRange()
            }
        }
    }
}
</script>

<style lang="scss">
    @import '~@fullcalendar/core/main.css';
    @import '~@fullcalendar/daygrid/main.css';
    @import '~@fullcalendar/list/main.css';

    .configuration-calendar .calendar-box {
        background: vt-color(white-light);
        box-shadow: 0 0 4px #cbcbcb;
    }

    .configuration-calendar .calendar-height {
        height: calc(100vh - 250px);
    }

    .configuration-calendar .fc-header-toolbar {
        @include mq(tablet-wide, max) {
            flex-direction: column;
            .fc-center {
                margin: 1rem 0;
            }
        }
    }

</style>
